import { useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { Send } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  CircularLoadingButton,
} from '@mint-lib/tw-components';

import useSendApprovalRequestMutation from '../../../api/mutations/useSendApprovalRequestMutation.js';
import {
  getNewRequestFormSchema,
  NewRequestFormValues,
} from '../../../types/newRequestForm';
import type { EntitySpecificFields } from '../../../types/shared.js';
import ApprovalFlowTab from './ApprovalFlowTab.jsx';
import RecipientsCommentsTab from './RecipientsCommentsTab.jsx';

type Props = EntitySpecificFields & {
  open: boolean;
  onClose: () => void;
};

const getTabList = (t: TFunction) => {
  return [
    {
      label: t('Approval flow'),
      a11yLabel: 'Approval flow tab',
      id: 'approval-flow',
      component: ApprovalFlowTab,
    },
    {
      label: t('Recipients & Comments'),
      a11yLabel: 'Recipients & Comments tab',
      id: 'recipients-comments',
      component: RecipientsCommentsTab,
    },
  ];
};

const SendNewRequestModal: FC<Props> = ({
  open,
  onClose,
  entityId,
  entityType,
  entityName,
  entitySource,
  entityMetadata,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');

  const formMethods = useForm<NewRequestFormValues>({
    mode: 'onChange',
    defaultValues: {
      flowId: '',
      carbonCopyEmails: [],
      comments: '',
    },
    resolver: zodResolver(getNewRequestFormSchema(t)),
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = formMethods;
  const {
    mutateAsync: sendApprovalRequest,
    isPending: isApprovalRequestSending,
  } = useSendApprovalRequestMutation();

  const tabList = getTabList(t);
  const [activeTab, setActiveTab] = useState(tabList[0].id);

  const closeModal = () => {
    reset();
    onClose();
  };

  const handleModalOpenChange = (isOpened: boolean) => {
    if (isOpened) {
      return;
    }

    closeModal();
  };

  const handleFormSubmit = handleSubmit(async (formData) => {
    try {
      await sendApprovalRequest({
        data: {
          approvalFlowId: formData.flowId,
          ccEmails: formData.carbonCopyEmails.map((option) => option.value),
          comment: formData.comments,
          entityId,
          entityType,
          entityName,
          entitySource,
          entityMetadata,
        },
      });

      enqueueSnackbar({
        variant: 'success',
        title: t('{{entity}} was sent for approval', { entity: 'Flight' }),
        removable: true,
      });
      closeModal();
    } catch {
      enqueueSnackbar({
        variant: 'error',
        title: t('{{entity}} was not sent for approval', { entity: 'Flight' }),
        removable: true,
      });
    }
  });

  return (
    <Dialog modal onOpenChange={handleModalOpenChange} open={open}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          size="m"
          className="outline-none"
          aria-describedby={undefined}
        >
          <FormProvider {...formMethods}>
            <form onSubmit={handleFormSubmit}>
              <DialogHeader>
                <DialogTitle>{t('Send new request')}</DialogTitle>
              </DialogHeader>
              <DialogContent>
                <div className="w-full">
                  <Tabs
                    orientation="horizontal"
                    value={activeTab}
                    onValueChange={setActiveTab}
                  >
                    <TabsList className="w-full">
                      {tabList.map(({ id, label, a11yLabel }) => {
                        return (
                          <TabsTrigger
                            key={id}
                            value={id}
                            data-dd-action-name={a11yLabel}
                          >
                            {label}
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                    <div className="mt-6">
                      {tabList.map(({ id, component: Component }) => {
                        return (
                          <TabsContent key={id} value={id}>
                            {<Component isVisible={open && activeTab === id} />}
                          </TabsContent>
                        );
                      })}
                    </div>
                  </Tabs>
                </div>
              </DialogContent>
              <DialogFooter>
                <Button
                  size="large"
                  variant="text"
                  type="button"
                  onClick={closeModal}
                  aria-label="Close the modal"
                  data-dd-action-name="Close the modal"
                >
                  {t('Cancel')}
                </Button>
                <Button
                  size="large"
                  variant="contained"
                  color="primary"
                  type="submit"
                  aria-label="Confirm action"
                  data-dd-action-name="Send new approval request"
                  disabled={!isValid || isApprovalRequestSending}
                >
                  {isApprovalRequestSending ? (
                    <CircularLoadingButton size="small" variant="contained" />
                  ) : (
                    <Send />
                  )}
                  {t('Send for approval')}
                </Button>
              </DialogFooter>
            </form>
          </FormProvider>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export default SendNewRequestModal;
